import { FrameDef } from './frames'
import {
  Filter,
  FrameRow,
  GroupRow,
  Metric,
  QueryResult,
  ViewConfig,
} from './view-config'

/**
 * Pure query engine: filter -> group -> aggregate -> sort/limit.
 * No React, no IO. Frame-generic — it operates on a frame's raw rows and reads
 * group ordering / the trend date from the FrameDef, so nothing here is tied to
 * the deal shape. Mirrors the SQL semantics of the dashboarding doc: sum/avg
 * skip nulls (count does not), aggregate results include a rollup total row.
 */

const DAY_MS = 86_400_000

/**
 * Default row/group cap when a view sets no explicit limit — keeps every preview
 * to a readable ten. An explicit `config.limit` overrides it (the Top-N knob),
 * and `limit: 0` means "all" (no cap). Time-bucket aggregates are exempt: their
 * x-axis is chronological, so capping would silently drop periods rather than
 * trim a long tail. Shared with the builder's Top-N default and the totals query.
 */
export const DEFAULT_VIEW_LIMIT = 10

const matches = (row: FrameRow, f: Filter): boolean => {
  switch (f.op) {
    case 'eq':
      return row[f.field] === f.value
    case 'neq':
      return row[f.field] !== f.value
    case 'in':
      // Empty selection = no constraint (Any), not "match nothing": deselecting
      // every value leaves the filter row in place without hiding all rows.
      return f.value.length === 0 || f.value.includes(row[f.field] as string)
    case 'gte':
      return row[f.field] != null && (row[f.field] as number) >= f.value
    case 'lte':
      return row[f.field] != null && (row[f.field] as number) <= f.value
    case 'within_days': {
      const iso = row[f.field]
      if (iso == null) return false
      // Invalid date strings produce NaN comparisons -> row excluded, matching SQL-ish "unparseable = no match".
      // delta >= 0 guards future dates: a future timestamp gives a negative delta which would otherwise always pass.
      const delta = Date.now() - new Date(iso as string).getTime()
      return delta >= 0 && delta <= f.value * DAY_MS
    }
    case 'date_after':
    case 'date_before': {
      // Calendar-date bound: compare the row's date (UTC day) against an
      // absolute yyyy-mm-dd, inclusive on both ends. A range is two of these on
      // one field. Day-string compare keeps both bounds inclusive regardless of
      // the row value's time-of-day. Unparseable / null dates never match.
      const iso = row[f.field]
      if (iso == null) return false
      const t = new Date(iso as string).getTime()
      if (Number.isNaN(t)) return false
      const day = new Date(t).toISOString().slice(0, 10)
      return f.op === 'date_after' ? day >= f.value : day <= f.value
    }
    case 'is_open':
      return row.datetime_closed == null
  }
}

const quarterKey = (iso: string): string => {
  const d = new Date(iso)
  return `${d.getUTCFullYear()}-Q${Math.floor(d.getUTCMonth() / 3) + 1}`
}

const monthKey = (iso: string): string => {
  const d = new Date(iso)
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
}

const groupKeyOf = (
  row: FrameRow,
  groupBy: NonNullable<ViewConfig['groupBy']>
): string => {
  const raw = row[groupBy.field]
  if (raw == null) return '(none)'
  if (groupBy.bucket === 'quarter') return quarterKey(raw as string)
  if (groupBy.bucket === 'month') return monthKey(raw as string)
  return String(raw)
}

const aggregate = (
  rows: FrameRow[],
  metrics: Metric[]
): Record<string, number> => {
  const out: Record<string, number> = {}
  for (const m of metrics) {
    if (m.agg === 'count') {
      out[m.label] = rows.length
      continue
    }
    if (m.agg === 'distinct') {
      // Unique count: how many distinct non-null values the field takes across
      // the rows. Works on any column (a rep name, a category, an id) — nulls
      // don't count, mirroring SQL COUNT(DISTINCT field).
      const seen = new Set<unknown>()
      for (const d of rows) {
        const v = d[m.field!]
        if (v != null) seen.add(v)
      }
      out[m.label] = seen.size
      continue
    }
    const values = rows
      .map((d) => d[m.field!] as number | null)
      .filter((v): v is number => v !== null)
    const sum = values.reduce((a, b) => a + b, 0)
    out[m.label] =
      m.agg === 'sum' ? sum : values.length === 0 ? 0 : sum / values.length
  }
  return out
}

/**
 * Orders group keys: an explicit frame ordering for the field (e.g. pipeline
 * stage order) when defined, chronological for time buckets (lexical works for
 * YYYY-MM / YYYY-Qn), else first-metric desc.
 */
const sortGroups = (
  groups: GroupRow[],
  groupBy: NonNullable<ViewConfig['groupBy']>,
  metrics: Metric[],
  frame: FrameDef
): GroupRow[] => {
  const order = frame.orderings?.[groupBy.field]
  if (order && !groupBy.bucket) {
    const rank = (k: string): number => {
      const i = order.indexOf(k)
      return i === -1 ? order.length : i
    }
    return [...groups].sort((a, b) => rank(a.key) - rank(b.key))
  }
  if (groupBy.bucket) {
    return [...groups].sort((a, b) => a.key.localeCompare(b.key))
  }
  const firstMetric = metrics[0].label
  return [...groups].sort(
    (a, b) => b.metrics[firstMetric] - a.metrics[firstMetric]
  )
}

/**
 * Replace each group's metric values with the running sum up to and including
 * that group (groups assumed already in chronological order). Series sub-values
 * accumulate per series key too, so a cumulative stacked/line chart stays
 * consistent with its segments.
 */
const runningTotal = (
  groups: GroupRow[],
  metrics: Metric[],
  seriesKeys?: string[]
): GroupRow[] => {
  const acc: Record<string, number> = {}
  const seriesAcc: Record<string, Record<string, number>> = {}
  return groups.map((g) => {
    const out: GroupRow = { key: g.key, metrics: {} }
    for (const m of metrics) {
      acc[m.label] = (acc[m.label] ?? 0) + (g.metrics[m.label] ?? 0)
      out.metrics[m.label] = acc[m.label]
    }
    if (g.series) {
      out.series = {}
      // Accumulate over the FULL set of series keys, not just those present in
      // this bucket: a key with no rows in a bucket must still carry its prior
      // cumulative forward, else the stacked total would shrink there (a running
      // total must be monotonic). Fall back to the bucket's own keys when the
      // caller didn't supply the union.
      const keys = seriesKeys ?? Object.keys(g.series)
      for (const sk of keys) {
        const cur = (seriesAcc[sk] ??= {})
        const vals = g.series[sk]
        out.series[sk] = {}
        for (const m of metrics) {
          cur[m.label] = (cur[m.label] ?? 0) + (vals?.[m.label] ?? 0)
          out.series[sk][m.label] = cur[m.label]
        }
      }
    }
    return out
  })
}

/** Period bucket key for the metric trend/sparkline. */
const periodKey = (
  iso: string,
  granularity: 'month' | 'quarter' | 'year'
): string => {
  if (granularity === 'year') return String(new Date(iso).getUTCFullYear())
  if (granularity === 'quarter') return quarterKey(iso)
  return monthKey(iso)
}

export type MetricSeriesPoint = { period: string; value: number }

/**
 * Time series of a metric report's measure, bucketed by the frame's trend date
 * into the given granularity (drives the metric sparkline + period-over-period
 * trend). Uses the same filters as the metric; the displayed measure is
 * `chartMetric`. Empty when the frame declares no trend date.
 */
export function metricSeries(
  config: ViewConfig,
  frame: FrameDef,
  granularity: 'month' | 'quarter' | 'year'
): MetricSeriesPoint[] {
  if (!config.metrics?.length || !frame.trendDate) return []
  const dateField = frame.trendDate
  const metrics = config.metrics
  const label = config.chartMetric ?? metrics[0].label
  const filtered = frame.records.filter((d) =>
    config.filters.every((f) => matches(d, f))
  )

  const buckets = new Map<string, FrameRow[]>()
  for (const row of filtered) {
    const iso = row[dateField]
    if (iso == null) continue
    const key = periodKey(iso as string, granularity)
    const bucket = buckets.get(key)
    if (bucket) bucket.push(row)
    else buckets.set(key, [row])
  }

  return [...buckets.entries()]
    .map(([period, rows]) => ({
      period,
      value: aggregate(rows, metrics)[label] ?? 0,
    }))
    .sort((a, b) => a.period.localeCompare(b.period))
}

export function runQuery(config: ViewConfig, frame: FrameDef): QueryResult {
  const filtered = frame.records.filter((d) =>
    config.filters.every((f) => matches(d, f))
  )

  if (config.kind === 'list') {
    let out = filtered
    if (config.sort) {
      const { by, dir } = config.sort
      out = [...out].sort((a, b) => {
        const av = a[by] as string | number | null
        const bv = b[by] as string | number | null
        if (av === null) return 1 // nulls last regardless of direction
        if (bv === null) return -1
        const cmp = av < bv ? -1 : av > bv ? 1 : 0
        return dir === 'desc' ? -cmp : cmp
      })
    }
    // Default-cap to ten unless the view asks otherwise; 0 means "all".
    const cap = config.limit ?? DEFAULT_VIEW_LIMIT
    if (cap > 0) out = out.slice(0, cap)
    return { kind: 'list', rows: out }
  }

  // aggregate
  // An aggregate must declare at least one metric; without one we can't compute
  // anything, so return an empty result instead of crashing.
  if (!config.metrics?.length) {
    return {
      kind: 'aggregate',
      groups: [],
      total: { key: 'TOTAL', metrics: {} },
    }
  }
  const metrics = config.metrics

  // Metric report: an aggregate with no groupBy resolves to a single rollup
  // value (no per-group rows). `total` carries that value.
  if (!config.groupBy) {
    return {
      kind: 'aggregate',
      groups: [],
      total: { key: 'TOTAL', metrics: aggregate(filtered, metrics) },
    }
  }
  const groupBy = config.groupBy
  const buckets = new Map<string, FrameRow[]>()
  for (const row of filtered) {
    const key = groupKeyOf(row, groupBy)
    const bucket = buckets.get(key)
    if (bucket) bucket.push(row)
    else buckets.set(key, [row])
  }

  const seriesKeys = config.seriesBy
    ? [...new Set(filtered.map((d) => String(d[config.seriesBy!])))].sort()
    : undefined

  const groups: GroupRow[] = [...buckets.entries()].map(([key, rows]) => {
    const group: GroupRow = { key, metrics: aggregate(rows, metrics) }
    if (config.seriesBy) {
      group.series = {}
      for (const sk of seriesKeys!) {
        const subset = rows.filter((d) => String(d[config.seriesBy!]) === sk)
        if (subset.length > 0) group.series[sk] = aggregate(subset, metrics)
      }
    }
    return group
  })

  const ordered = sortGroups(groups, groupBy, metrics, frame)
  // Running total: accumulate each metric (and each series value) across the
  // ordered buckets so the chart reads as a cumulative YTD line, not per-period
  // spikes. Time-series only; the rollup `total` below stays the grand total.
  const sorted =
    config.cumulative && groupBy.bucket
      ? runningTotal(ordered, metrics, seriesKeys)
      : ordered
  // Top-N: keep the leading groups in sort order (e.g. top 10 owners by revenue).
  // Categorical groups default-cap to ten; time buckets don't (chronological — a
  // cap would drop periods, not a tail). An explicit limit overrides; 0 = all.
  // The rollup total still reflects every row, not just the shown groups.
  const cap = config.limit ?? (groupBy.bucket ? 0 : DEFAULT_VIEW_LIMIT)
  const limited = cap > 0 ? sorted.slice(0, cap) : sorted

  // Rollup total over every filtered row. With a series split, also sub-aggregate
  // the total per series key (re-aggregating, not summing the per-group values)
  // so a pivot's Total row is correct for any agg — avg of all rows, not an avg
  // of monthly avgs.
  const total: GroupRow = {
    key: 'TOTAL',
    metrics: aggregate(filtered, metrics),
  }
  if (config.seriesBy && seriesKeys) {
    total.series = {}
    for (const sk of seriesKeys) {
      const subset = filtered.filter((d) => String(d[config.seriesBy!]) === sk)
      if (subset.length > 0) total.series[sk] = aggregate(subset, metrics)
    }
  }

  return {
    kind: 'aggregate',
    groups: limited,
    total,
    seriesKeys,
  }
}
